import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  HelpCircle,
  Mail,
  Monitor,
  Mouse,
  Phone,
  Search,
  Sparkles,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'

// ─── Tutorial step data ──────────────────────────────────────────────────────
interface TutorialStep {
  title: string
  desc: string
  icon: 'monitor' | 'mouse' | 'sparkles' | 'file'
}

interface TutorialData {
  title: string
  desc: string
  icon: string
  steps: TutorialStep[]
}

interface FaqItem {
  q: string
  a: string
}

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '帮助中心',
    heroTitle: '帮助中心',
    heroDesc: '查找答案、教程和联系方式',
    searchPlaceholder: '搜索问题...',
    tutorialsTitle: '快速入门教程',
    backToHelp: '返回帮助中心',
    stepLabel: '步骤',
    tutorials: [
      {
        title: '快速入门指南',
        desc: '了解 AI 律师助手的基本功能和使用方法',
        icon: 'book',
        steps: [
          { title: '登录系统', desc: '打开浏览器访问系统地址，使用您的账号密码登录。登录后您将看到工作台概览，包含案件统计、知识库摘要和最近活动。', icon: 'monitor' },
          { title: '浏览工作台', desc: '工作台显示您的案件总数、知识库文件数量和待处理事项。左侧导航栏提供案件列表、分类知识库、社区法律基金会等功能入口。', icon: 'mouse' },
          { title: '发起AI对话', desc: '在任意案件详情页或社区法律基金会中，您可以直接向 AI 助手提问。AI 将基于知识库内容为您提供专业的法律分析和建议。', icon: 'sparkles' },
          { title: '查看分析结果', desc: 'AI 助手的回答会引用具体的判例和法律条文来源，您可以点击来源查看原文。对话记录会自动保存，方便日后回顾。', icon: 'file' },
        ],
      },
      {
        title: '案件管理教程',
        desc: '学习如何创建、管理和分析案件',
        icon: 'file',
        steps: [
          { title: '创建新案件', desc: '在案件列表页面点击"新建案件"按钮，填写案件名称、类型、客户信息等基本资料，系统会自动生成案件编号。', icon: 'mouse' },
          { title: '上传案件文件', desc: '进入案件详情页，在"文件"标签下点击上传按钮。支持 PDF、Word、图片等格式，系统会自动进行 OCR 识别和文本提取。', icon: 'file' },
          { title: '使用AI分析', desc: '在案件详情的"AI助手"面板中提问，AI 将结合该案件的文件内容和知识库进行分析，为您提供法律意见和策略建议。', icon: 'sparkles' },
          { title: '管理案件状态', desc: '通过案件列表的状态筛选器，您可以按"进行中"、"已完成"、"已归档"等状态管理案件，保持工作有序。', icon: 'monitor' },
        ],
      },
      {
        title: '知识库建设指南',
        desc: '构建和维护您的分类知识库',
        icon: 'book',
        steps: [
          { title: '了解分类体系', desc: '系统预设了离婚、合同、财产、债务、劳动法、金融法规等分类。每个分类下存储对应领域的法律法规、判例和参考资料。', icon: 'monitor' },
          { title: '上传知识文件', desc: '在分类知识库中选择目标分类，点击上传文件。系统支持批量上传，会自动解析文档内容并建立向量索引，支持语义搜索。', icon: 'file' },
          { title: '搜索和引用', desc: '上传完成后，AI 助手在分析时会自动检索相关知识。您也可以使用搜索功能手动查找特定判例或法律条文。', icon: 'sparkles' },
          { title: '维护和更新', desc: '定期更新知识库内容，删除过时的文件，上传最新的法律修订和判例，确保 AI 分析基于最新法律框架。', icon: 'mouse' },
        ],
      },
      {
        title: 'AI 分析技巧',
        desc: '高效利用 AI 进行案件分析和文书生成',
        icon: 'ai',
        steps: [
          { title: '提出精准问题', desc: '向 AI 提问时尽量具体，例如"根据马来西亚劳动法第20条，雇主在这种情况下是否构成不当解雇？"比"这个案件怎么办？"更能获得精准回答。', icon: 'sparkles' },
          { title: '利用上下文', desc: 'AI 助手会记住同一对话中的上下文。您可以先描述案件背景，再逐步深入提问，AI 会综合之前的对话内容给出连贯的分析。', icon: 'mouse' },
          { title: '验证来源引用', desc: 'AI 回答中会标注引用来源（判例/法规）。请务必点击查看原文来验证引用的准确性，确保法律依据的可靠性。', icon: 'file' },
          { title: '生成法律文书', desc: '您可以要求 AI 生成法律意见书、诉状摘要、合同条款分析等文书。指定格式要求和语言（中文/英文），AI 会按照当地法律规范生成。', icon: 'monitor' },
        ],
      },
    ] as TutorialData[],
    faqTitle: '常见问题',
    faqs: [
      { q: '如何创建新案件？', a: '在左侧导航栏点击"案件列表"，然后点击页面右上角的"新建案件"按钮。填写案件名称、类型（如民事、刑事、商事等）、客户信息和案件描述，点击确认即可创建。新案件会自动出现在您的案件列表中，状态为"规划中"。' },
      { q: '如何上传知识文件到分类知识库？', a: '进入"分类知识库"页面，选择目标分类（如离婚、合同、财产等），点击上传按钮。支持 PDF、Word (.docx)、纯文本 (.txt) 和图片格式。上传后系统会自动进行 OCR 识别（如为图片）、文本提取和向量索引构建，通常在 30 秒内完成处理。处理完成后，该文件的内容即可被 AI 助手检索引用。' },
      { q: 'AI 分析的准确性如何保证？', a: '我们的 AI 分析基于 RAG（检索增强生成）技术，每次回答都会从您的知识库中检索最相关的法律文档和判例作为依据。回答中会明确标注引用来源，方便您验证。但请注意，AI 分析仅供参考，不构成正式法律意见。建议在重要决策前由专业律师审核 AI 的分析结论。' },
      { q: '支持生成哪些类型的法律文书？', a: '目前支持生成以下类型的法律文书：法律意见书、诉状摘要、合同审查报告、案件分析备忘录、庭审准备提纲等。您可以在 AI 对话中指定文书类型、格式要求和输出语言（支持中文和英文/马来西亚法庭标准格式）。生成的文书可以复制或导出为文档格式。' },
      { q: '如何导出生成的文书？', a: '在 AI 助手的对话中，当 AI 生成文书内容后，您可以直接选中文本复制。后续版本将支持一键导出为 Word (.docx) 和 PDF 格式。目前建议将生成内容复制到您常用的文档编辑器中进行进一步排版和修改。' },
      { q: '对话记录会自动保存吗？', a: '是的，所有对话记录都会自动保存。案件详情中的 AI 对话会关联到对应案件，您可以随时回到案件详情查看历史对话。社区法律基金会的 AI 助手对话也有独立的历史记录管理，支持按主题分类查看和删除。系统会按时间顺序保存所有交互记录。' },
    ] as FaqItem[],
    contactTitle: '联系我们',
    contactDesc: '如果您的问题仍未解决，请联系我们的支持团队',
    contactEmail: 'support@ailawyer.my',
    contactPhone: '+60 12-345 6789',
    workHours: '工作时间：周一至周五 9:00 - 18:00（马来西亚标准时间）',
    footerLine1: 'AI Lawyer MVP Version 1.0.0',
    footerLine2: 'Copyright © 2024 AI Lawyer. All rights reserved.',
  },
  en: {
    pageTitle: 'Help Center',
    heroTitle: 'Help Center',
    heroDesc: 'Find answers, tutorials and contact information',
    searchPlaceholder: 'Search questions...',
    tutorialsTitle: 'Quick Start Tutorials',
    backToHelp: 'Back to Help Center',
    stepLabel: 'Step',
    tutorials: [
      {
        title: 'Quick Start Guide',
        desc: 'Learn the basic features and usage of AI Lawyer',
        icon: 'book',
        steps: [
          { title: 'Login', desc: 'Open your browser and navigate to the system URL. Log in with your credentials. After login, you will see the dashboard overview with case statistics, knowledge base summary, and recent activities.', icon: 'monitor' },
          { title: 'Explore Dashboard', desc: 'The dashboard shows your total cases, knowledge base file count, and pending items. The left sidebar provides access to case list, knowledge base, legal base, and other features.', icon: 'mouse' },
          { title: 'Start AI Conversation', desc: 'In any case detail page or the legal knowledge base, you can directly ask the AI assistant questions. The AI will provide professional legal analysis based on your knowledge base content.', icon: 'sparkles' },
          { title: 'Review Results', desc: 'The AI assistant\'s answers cite specific precedents and legal provisions. You can click on sources to view the original text. Conversation records are automatically saved for future reference.', icon: 'file' },
        ],
      },
      {
        title: 'Case Management',
        desc: 'Learn how to create, manage and analyze cases',
        icon: 'file',
        steps: [
          { title: 'Create New Case', desc: 'On the case list page, click "New Case" button. Fill in the case name, type, client information, and the system will automatically generate a case number.', icon: 'mouse' },
          { title: 'Upload Case Files', desc: 'Enter the case detail page, under the "Files" tab click upload. Supports PDF, Word, and images with automatic OCR recognition and text extraction.', icon: 'file' },
          { title: 'Use AI Analysis', desc: 'In the case detail "AI Assistant" panel, ask questions. The AI will analyze using the case files and knowledge base to provide legal opinions and strategy suggestions.', icon: 'sparkles' },
          { title: 'Manage Case Status', desc: 'Use the status filter in the case list to manage cases by "In Progress", "Completed", "Archived" status to keep work organized.', icon: 'monitor' },
        ],
      },
      {
        title: 'Knowledge Base Guide',
        desc: 'Build and maintain your categorized knowledge base',
        icon: 'book',
        steps: [
          { title: 'Understand Categories', desc: 'The system has preset categories: Divorce, Contract, Property, Debt, Employment Law, Financial Regulations, etc. Each stores relevant legal materials and precedents.', icon: 'monitor' },
          { title: 'Upload Knowledge Files', desc: 'In the knowledge base, select target category and click upload. Supports batch upload with automatic parsing and vector indexing for semantic search.', icon: 'file' },
          { title: 'Search and Reference', desc: 'Once uploaded, the AI assistant automatically retrieves relevant knowledge during analysis. You can also manually search for specific precedents or statutes.', icon: 'sparkles' },
          { title: 'Maintain and Update', desc: 'Regularly update your knowledge base content, remove outdated files, and upload latest legal amendments to ensure AI analysis uses the current legal framework.', icon: 'mouse' },
        ],
      },
      {
        title: 'AI Analysis Tips',
        desc: 'Efficiently use AI for case analysis and document generation',
        icon: 'ai',
        steps: [
          { title: 'Ask Precise Questions', desc: 'Be specific when asking AI, e.g. "Under Section 20 of Malaysia Employment Act, does this constitute wrongful dismissal?" gets better results than "What about this case?"', icon: 'sparkles' },
          { title: 'Use Context', desc: 'The AI remembers context within a conversation. Describe the case background first, then ask progressively deeper questions for coherent analysis.', icon: 'mouse' },
          { title: 'Verify Citations', desc: 'AI responses include source citations (precedents/statutes). Always click to view originals and verify accuracy of legal references.', icon: 'file' },
          { title: 'Generate Documents', desc: 'Ask AI to generate legal opinions, pleading summaries, contract analyses, etc. Specify format and language requirements for output following local legal standards.', icon: 'monitor' },
        ],
      },
    ] as TutorialData[],
    faqTitle: 'Frequently Asked Questions',
    faqs: [
      { q: 'How to create a new case?', a: 'Click "Case List" in the left sidebar, then click the "New Case" button at the top right. Fill in the case name, type (civil, criminal, commercial, etc.), client information, and description. Click confirm to create. The new case will appear in your case list with "Planning" status.' },
      { q: 'How to upload files to the knowledge base?', a: 'Go to "Knowledge Base" page, select the target category (Divorce, Contract, Property, etc.), and click upload. Supports PDF, Word (.docx), plain text (.txt), and image formats. The system automatically performs OCR (for images), text extraction, and vector index building, usually completing within 30 seconds.' },
      { q: 'How is AI analysis accuracy ensured?', a: 'Our AI analysis is based on RAG (Retrieval-Augmented Generation) technology. Each answer retrieves the most relevant legal documents and precedents from your knowledge base as evidence. Sources are clearly cited for verification. Note that AI analysis is for reference only and does not constitute formal legal advice.' },
      { q: 'What types of legal documents can be generated?', a: 'Currently supports: legal opinions, pleading summaries, contract review reports, case analysis memos, trial preparation outlines, and more. Specify document type, format requirements, and output language (Chinese or English/Malaysian court standard) in the AI conversation.' },
      { q: 'How to export generated documents?', a: 'After the AI generates document content in the conversation, you can select and copy the text directly. Future versions will support one-click export to Word (.docx) and PDF formats. Currently, we recommend copying generated content to your preferred document editor for further formatting.' },
      { q: 'Are chat records saved automatically?', a: 'Yes, all conversation records are automatically saved. AI conversations in case details are linked to the corresponding case. The legal knowledge base AI assistant also has independent history management with topic-based categorization. All interaction records are saved chronologically.' },
    ] as FaqItem[],
    contactTitle: 'Contact Us',
    contactDesc: "If your question is still unresolved, please contact our support team",
    contactEmail: 'support@ailawyer.my',
    contactPhone: '+60 12-345 6789',
    workHours: 'Working hours: Mon-Fri 9:00 - 18:00 (Malaysia Standard Time)',
    footerLine1: 'AI Lawyer MVP Version 1.0.0',
    footerLine2: 'Copyright © 2024 AI Lawyer. All rights reserved.',
  },
}

const ICON_MAP = {
  book: BookOpen,
  file: FileText,
  ai: Sparkles,
}

const STEP_ICON_MAP = {
  monitor: Monitor,
  mouse: Mouse,
  sparkles: Sparkles,
  file: FileText,
}

// ─── Streaming text component ────────────────────────────────────────────────
function StreamingText({ text, active }: { text: string; active: boolean }) {
  const [displayed, setDisplayed] = useState('')
  const [done, setDone] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!active) {
      setDisplayed('')
      setDone(false)
      return
    }
    setDisplayed('')
    setDone(false)
    let i = 0
    intervalRef.current = setInterval(() => {
      i++
      if (i >= text.length) {
        setDisplayed(text)
        setDone(true)
        if (intervalRef.current) clearInterval(intervalRef.current)
      } else {
        setDisplayed(text.slice(0, i))
      }
    }, 20)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [active, text])

  if (!active) return null
  return (
    <p className="text-sm text-brand-600 leading-relaxed mt-2 pl-10">
      {displayed}
      {!done && <span className="inline-block w-0.5 h-4 bg-blue-500 ml-0.5 animate-pulse align-middle" />}
    </p>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────
export function Help() {
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh

  const [activeTutorial, setActiveTutorial] = useState<number | null>(null)
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const toggleFaq = useCallback((idx: number) => {
    setExpandedFaq((prev) => (prev === idx ? null : idx))
  }, [])

  // Filter FAQs based on search
  const filteredFaqs = searchQuery.trim()
    ? t.faqs.filter(
        (faq) =>
          faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.a.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : t.faqs

  // ─── Tutorial detail view ────────────────────────────────────────────────
  if (activeTutorial !== null) {
    const tut = t.tutorials[activeTutorial]
    return (
      <>
        <TopBar title={t.pageTitle} />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 py-8">
            {/* Back button */}
            <button
              type="button"
              onClick={() => setActiveTutorial(null)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToHelp}
            </button>

            {/* Tutorial header */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-brand-800">{tut.title}</h2>
              <p className="text-sm text-brand-400 mt-1">{tut.desc}</p>
            </div>

            {/* Steps */}
            <div className="space-y-6">
              {tut.steps.map((step, idx) => {
                const StepIcon = STEP_ICON_MAP[step.icon] ?? Monitor
                return (
                  <div
                    key={idx}
                    className="relative pl-8 pb-6 border-l-2 border-blue-200 last:border-0 last:pb-0"
                  >
                    {/* Step number circle */}
                    <div className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-[11px] font-bold text-white">
                        {idx + 1}
                      </span>
                    </div>

                    {/* Step content card */}
                    <div className="ml-4 bg-white border border-brand-200 rounded-xl p-5 shadow-sm">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                          <StepIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <span className="text-[10px] uppercase tracking-wider text-brand-400 font-medium">
                            {t.stepLabel} {idx + 1}
                          </span>
                          <h4 className="text-sm font-semibold text-brand-800">
                            {step.title}
                          </h4>
                        </div>
                      </div>
                      <p className="text-sm text-brand-600 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </>
    )
  }

  // ─── Main help center view ─────────────────────────────────────────────────
  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8">
          {/* Hero */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
              <HelpCircle className="w-7 h-7 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-brand-800">{t.heroTitle}</h2>
            <p className="text-sm text-brand-400 mt-1">{t.heroDesc}</p>
          </div>

          {/* 搜索 */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
            />
          </div>

          {/* 快速入门教程 */}
          <h3 className="text-base font-semibold text-brand-800 mb-3">
            {t.tutorialsTitle}
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-8">
            {t.tutorials.map((tut, idx) => {
              const Icon = ICON_MAP[tut.icon as keyof typeof ICON_MAP] ?? BookOpen
              return (
                <div
                  key={idx}
                  onClick={() => setActiveTutorial(idx)}
                  className="flex items-center gap-3 p-4 rounded-xl border border-brand-200 hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-brand-800">
                      {tut.title}
                    </div>
                    <p className="text-xs text-brand-400 mt-0.5 truncate">
                      {tut.desc}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-brand-300 group-hover:text-blue-500 flex-shrink-0 transition-colors" />
                </div>
              )
            })}
          </div>

          {/* 常见问题 */}
          <h3 className="text-base font-semibold text-brand-800 mb-3">
            {t.faqTitle}
          </h3>
          <div className="space-y-2 mb-10">
            {filteredFaqs.map((faq) => {
              const realIdx = t.faqs.indexOf(faq)
              const isExpanded = expandedFaq === realIdx
              return (
                <div key={realIdx}>
                  <div
                    onClick={() => toggleFaq(realIdx)}
                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors cursor-pointer group ${
                      isExpanded
                        ? 'border-blue-300 bg-blue-50/40'
                        : 'border-brand-200 hover:border-brand-300'
                    }`}
                  >
                    <span className="w-7 h-7 rounded-lg bg-brand-100 flex items-center justify-center text-sm font-semibold text-brand-500 flex-shrink-0">
                      {realIdx + 1}
                    </span>
                    <span className="text-sm text-brand-700 flex-1">
                      {faq.q}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-brand-300 flex-shrink-0 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180 text-blue-500' : 'group-hover:text-brand-500'
                      }`}
                    />
                  </div>
                  <StreamingText text={faq.a} active={isExpanded} />
                </div>
              )
            })}
          </div>

          {/* 联系我们 */}
          <div className="bg-brand-50 rounded-xl p-6 text-center mb-8">
            <h3 className="text-base font-semibold text-brand-800 mb-1">
              {t.contactTitle}
            </h3>
            <p className="text-sm text-brand-400 mb-4">{t.contactDesc}</p>
            <div className="flex items-center justify-center gap-4">
              <a
                href={`mailto:${t.contactEmail}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-brand-300 text-sm text-brand-700 hover:bg-white transition-colors"
              >
                <Mail className="w-4 h-4" />
                {t.contactEmail}
              </a>
              <a
                href={`tel:${t.contactPhone.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-brand-300 text-sm text-brand-700 hover:bg-white transition-colors"
              >
                <Phone className="w-4 h-4" />
                {t.contactPhone}
              </a>
            </div>
            <p className="text-xs text-brand-400 mt-3">{t.workHours}</p>
          </div>

          {/* 页脚 */}
          <div className="text-center text-xs text-brand-400 pb-4">
            <p>{t.footerLine1}</p>
            <p className="mt-0.5">{t.footerLine2}</p>
          </div>
        </div>
      </div>
    </>
  )
}
